import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Add, AddCircleOutline } from '@mui/icons-material'

import { IFilterProfileLite } from '$/types'
import { useGetCurrentUserIdQuery } from 'app/services/api/users'
import { normalizedFilterProfilesSelectors } from 'app/entities/store/filterProfilesSelectors'
import {
  LOCAL_PROFILE_ID,
  UNSAVED_PROFILE_NAME
} from 'app/layout/Navbar/Filters/FilterModal/constants'
import {
  selectEditInProgress,
  selectStagingProfile
} from 'app/layout/Navbar/Filters/globalFilterSelectors'
import OverlayScrollbar from 'common/components/OverlayScrollBar'
import { useHasPermissions } from 'common/hooks/useHasPermissions'
import { getUnsavedProfile } from 'common/utils/filter/systemScope'

import { setPopupNotification } from 'features/Settings/settingsSlice'

import ProfileListItem from './ProfileListItem'

interface IProfileSelector {
  onCreate: () => void
  onSelect: (id: string) => void
  onDelete: (id: string) => void
}

const ProfileSelector = ({
  onCreate,
  onSelect,
  onDelete
}: IProfileSelector) => {
  const dispatch = useDispatch()
  // Use permissions hook to check for explicit create permission
  const hasPermission = useHasPermissions()
  const canCreateProfile = hasPermission(['filterprofile:create'])

  const { currentData: currentUserId } = useGetCurrentUserIdQuery()

  const filterProfileListNormalized = useSelector(
    normalizedFilterProfilesSelectors.selectAll
  )

  const stagingProfile = useSelector(selectStagingProfile)
  const editInProgress = useSelector(selectEditInProgress)

  const [privateProfiles, setPrivateProfiles] = useState<IFilterProfileLite[]>(
    []
  )

  const [publicProfiles, setPublicProfiles] = useState<IFilterProfileLite[]>([])

  useEffect(() => {
    const privateProfiles = filterProfileListNormalized.filter(
      (p) => p.isPrivate
    )

    const isNewProfilePresent = filterProfileListNormalized.some(
      (profile) => profile.id === LOCAL_PROFILE_ID
    )

    const publicProfiles = filterProfileListNormalized.filter(
      (p) => !p.isPrivate
    )

    if (stagingProfile) {
      //we no longer move unsaved profile into public/network profile section
      if (stagingProfile.id === LOCAL_PROFILE_ID && !isNewProfilePresent) {
        privateProfiles.unshift(stagingProfile)
      }
    }

    setPrivateProfiles(privateProfiles)
    setPublicProfiles(publicProfiles)
  }, [dispatch, filterProfileListNormalized, stagingProfile])

  const renderPrivateProfileList = (
    privateProfiles: IFilterProfileLite[]
  ): JSX.Element | JSX.Element[] => {
    if (privateProfiles.length > 0) {
      const privateProfilesList = privateProfiles.map((profile) => {
        if (
          profile.id === LOCAL_PROFILE_ID ||
          profile.name === UNSAVED_PROFILE_NAME
        ) {
          return (
            <ProfileListItem
              key={profile.id.toString()}
              itemKey={profile.id.toString()}
              isSelected={stagingProfile?.id.toString()}
              onDefaultSelect={onSelect}
            >
              <i>
                {profile.name.trim().length > 0 ? profile.name : 'Invalid Name'}
              </i>
            </ProfileListItem>
          )
        }

        return (
          <ProfileListItem
            key={profile.id.toString()}
            itemKey={profile.id.toString()}
            isSelected={stagingProfile?.id.toString()}
            onDefaultSelect={onSelect}
            dropdownSectionRenderer={() => (
              <button
                className='w-32 p-4 text-left hover:bg-gray-100'
                onClick={() => onDelete(profile.id.toString())}
              >
                DELETE
              </button>
            )}
          >
            {profile.id === stagingProfile?.id && editInProgress ? (
              <i>{profile.name}</i>
            ) : (
              profile.name
            )}
          </ProfileListItem>
        )
      })

      // Move unsaved profile to top
      const unsavedProfileId = getUnsavedProfile(
        filterProfileListNormalized
      )?.id

      if (unsavedProfileId != null) {
        const item = privateProfilesList.find(
          (p) => p.key === unsavedProfileId.toString()
        )

        if (item != null) {
          const index = privateProfilesList.indexOf(item)

          privateProfilesList.splice(index, 1)
          privateProfilesList.unshift(item)
        }
      }

      return privateProfilesList
    }

    return (
      <button className='m-2 text-lighthouseBlue' onClick={handleAttemptCreate}>
        <span className='text-lg'>
          <Add style={{ fontSize: 'inherit' }} />
        </span>
        <span className='text-sm font-medium'>Create a profile</span>
      </button>
    )
  }

  const renderPublicProfileList = (
    sharedProfiles: IFilterProfileLite[]
  ): JSX.Element[] => {
    const publicProfileList = sharedProfiles.map((profile) => {
      // REVIEW: Possible for currentUserId to be uninitialized?
      const isCurrentUserOwnedProfile = profile.userId === currentUserId
      const isSavedProfile = profile.id !== LOCAL_PROFILE_ID

      if (isCurrentUserOwnedProfile && isSavedProfile) {
        return (
          <ProfileListItem
            key={profile.id.toString()}
            itemKey={profile.id.toString()}
            isSelected={stagingProfile?.id.toString()}
            onDefaultSelect={onSelect}
            dropdownSectionRenderer={() => (
              <button
                className='w-32 p-4 text-left hover:bg-gray-100'
                onClick={() => onDelete(profile.id.toString())}
              >
                DELETE
              </button>
            )}
          >
            {profile.id === stagingProfile?.id && editInProgress ? (
              <i>{profile.name}</i>
            ) : (
              profile.name
            )}
          </ProfileListItem>
        )
      }

      return (
        <ProfileListItem
          key={profile.id.toString()}
          itemKey={profile.id.toString()}
          isSelected={stagingProfile?.id.toString()}
          onDefaultSelect={onSelect}
        >
          {profile.name}
        </ProfileListItem>
      )
    })

    return publicProfileList
  }

  const handleAttemptCreate = (): void => {
    if (!canCreateProfile) {
      dispatch(
        setPopupNotification({
          isOpen: true,
          title: 'Access Denied',
          message:
            "You don't have the proper permissions to access this module. Speak with your administrator if you require access."
        })
      )

      return
    }

    onCreate()
  }

  return (
    <div className='flex flex-col border-r-2 border-gray-200'>
      <div
        className={[
          'flex items-center justify-between',
          'py-3 pl-6 pr-4',
          'bg-gray-100',
          'font-medium'
        ].join(' ')}
      >
        <span className='pr-4'>Profiles</span>
        <button className='text-lighthouseBlue' onClick={handleAttemptCreate}>
          <AddCircleOutline fontSize='small' />
          <span className='sr-only'>Add New</span>
        </button>
      </div>

      <div className='mx-4 mt-6 flex flex-1 flex-col overflow-auto'>
        <div
          className={[
            'mx-2 mb-4',
            'font-medium tracking-wider',
            'text-gray-500'
          ].join(' ')}
        >
          MY PROFILES
        </div>
        <OverlayScrollbar>
          <div className='pr-2'>
            {renderPrivateProfileList(privateProfiles)}
          </div>
        </OverlayScrollbar>
      </div>

      <div className='mx-4 my-6 flex flex-1 flex-col overflow-auto'>
        <div
          className={[
            'mb-4 ml-2 mr-8',
            'font-medium',
            'tracking-wider text-gray-500'
          ].join(' ')}
        >
          NETWORK PROFILES
        </div>
        <OverlayScrollbar>
          <div className='pr-2'>{renderPublicProfileList(publicProfiles)}</div>
        </OverlayScrollbar>
      </div>
    </div>
  )
}

export default ProfileSelector
